import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Link2Off, UserRound } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { buttonVariants } from '@/components/common/Button'
import { ErrorState } from '@/components/common/ErrorState'
import { PageSkeleton } from '@/components/common/PageSkeleton'
import { essKeys, getEmployeeLink, type EmployeeLink } from '@/api/ess'

/** The caller's link to an employee record — cached for the session, refreshed on focus. */
export function useEmployeeLink(enabled = true) {
  const { user } = useAuth()
  return useQuery({
    queryKey: [...essKeys.link, user?.id ?? 'anon'],
    queryFn: getEmployeeLink,
    enabled: enabled && Boolean(user),
    staleTime: 5 * 60_000,
    meta: { inlineError: true },
  })
}

const HELP_SUBJECT = 'Please link my login to my employee profile'

/**
 * THE "login not linked" state (Addendum E4): one friendly explanation, never an empty table
 * or a 403. HR roles are told where to do it themselves; everyone else gets a one-click
 * helpdesk request. Logins that never have an employee record (CA partner, platform owner)
 * are told plainly that My space is for employees.
 */
export function NotLinkedNotice({ link, compact = false }: { link?: EmployeeLink; compact?: boolean }) {
  const { user, hasRole } = useAuth()
  const isHr = hasRole(['HR_ADMIN', 'HR_EXECUTIVE'])
  const expects = link?.expects_employee ?? !['CA_PARTNER', 'SUPER_ADMIN'].includes(user?.role ?? '')
  const title = expects ? "Your login isn't linked to an employee profile yet" : 'My space is for employees'
  const message = !expects
    ? 'This login is not an employee login, so it has no payslips, leave or attendance of its own. Everything else in the app works as usual.'
    : isHr
      ? 'Payslips, leave, attendance and the rest of My space come from an employee record. Open your record in Employees and use Portal access → Link existing login to connect this login.'
      : 'Payslips, leave, attendance and the rest of My space come from your employee record. HR can link it from Employees → Portal access — it takes them a minute.'
  return (
    <div role="status" data-state="not-linked"
      className={compact ? 'flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-e1 sm:flex-row sm:items-center'
        : 'mx-auto flex max-w-xl flex-col items-center px-6 py-14 text-center'}>
      <div className={compact ? 'grid size-10 shrink-0 place-items-center rounded-lg bg-warning-subtle text-warning-content'
        : 'mb-4 grid size-12 place-items-center rounded-xl bg-warning-subtle text-warning-content'}>
        {expects ? <Link2Off className="size-5" /> : <UserRound className="size-5" />}
      </div>
      <div className={compact ? 'min-w-0 flex-1' : ''}>
        <h2 className={compact ? 'text-sm font-semibold text-foreground' : 'text-lg font-semibold text-foreground'}>{title}</h2>
        <p className={compact ? 'mt-0.5 text-[13px] leading-5 text-muted-foreground' : 'mt-1.5 text-sm leading-6 text-muted-foreground'}>{message}</p>
      </div>
      {expects && (
        <div className={compact ? 'flex shrink-0 flex-wrap gap-2' : 'mt-6 flex flex-wrap items-center justify-center gap-2'}>
          {isHr
            ? <Link to="/employees" className={buttonVariants({ variant: 'primary', size: compact ? 'sm' : 'md' })}>Open Employees</Link>
            : <Link to={`/helpdesk?new=1&subject=${encodeURIComponent(HELP_SUBJECT)}`} className={buttonVariants({ variant: 'primary', size: compact ? 'sm' : 'md' })}>Ask HR to link it</Link>}
          {!compact && <Link to="/dashboard" className={buttonVariants({ variant: 'outline' })}>Go to dashboard</Link>}
        </div>
      )}
    </div>
  )
}

/**
 * Wrap a self-service page: renders it only when the login has an employee record, so an
 * unlinked login never fires the page's queries (no 403 noise, no empty tables).
 */
export function EssGate({ children }: { children: ReactNode }) {
  const q = useEmployeeLink()
  if (q.isLoading) return <PageSkeleton />
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} retrying={q.isFetching} />
  if (!q.data?.linked) return <NotLinkedNotice link={q.data} />
  return <>{children}</>
}
